import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Users, Plus, MapPin, Calendar, Euro, Send, X, CheckCircle2 } from "lucide-react";

const euro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const TRADES = { bache: "Bâcherie", sellerie: "Sellerie", coussin: "Coussins", capote: "Capote/Taud", autre: "Autre" };
const STATUS = {
  ouverte: { label: "Ouverte", cls: "bg-sky-50 text-sky-700" },
  attribuee: { label: "Attribuée", cls: "bg-amber-50 text-amber-700" },
  en_cours: { label: "En cours", cls: "bg-indigo-50 text-indigo-700" },
  terminee: { label: "Terminée", cls: "bg-emerald-50 text-emerald-700" },
  annulee: { label: "Annulée", cls: "bg-rose-50 text-rose-700" },
};
const NEXT = { attribuee: "en_cours", en_cours: "terminee" };

export default function Reseau() {
  const [tab, setTab] = useState("marketplace");
  const [missions, setMissions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", trade: "bache", budget: 0, city: "", deadline: "" });

  const load = () => api.get(`/missions?scope=${tab}`).then((r) => setMissions(r.data)).catch(() => {});
  useEffect(() => { load(); }, [tab]);

  const create = async () => {
    if (!form.title) return toast.error("Titre requis");
    await api.post("/missions", { ...form, budget: parseFloat(form.budget) || 0, origin_url: window.location.origin });
    toast.success("Mission publiée");
    setShowForm(false);
    setForm({ title: "", description: "", trade: "bache", budget: 0, city: "", deadline: "" });
    if (tab === "mine") load();
  };

  const apply = async (id) => {
    try {
      await api.post(`/missions/${id}/apply`);
      toast.success("Mission acceptée ! Retrouvez-la dans « Mes missions »");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const advance = async (m) => {
    const next = NEXT[m.status];
    if (!next) return;
    await api.put(`/missions/${m.id}/status`, { status: next });
    toast.success("Statut mis à jour");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center">
            <Users className="w-5 h-5 text-sky-400" />
          </div>
          <div>
            <h1 className="text-3xl font-display font-bold text-navy">Réseau Sous-traitance</h1>
            <p className="text-slate-500">Proposez ou acceptez des missions entre ateliers pros</p>
          </div>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="new-mission-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-400 text-navy font-semibold hover:bg-sky-500 transition-colors">
          <Plus className="w-4 h-4" /> Proposer une mission
        </button>
      </div>

      <div className="flex gap-2">
        {[["marketplace", "Missions disponibles"], ["mine", "Mes missions"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${tab === k ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-sky-300"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {missions.map((m) => (
          <div key={m.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col" data-testid={`mission-${m.id}`}>
            <div className="flex items-start justify-between gap-2">
              <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">{TRADES[m.trade] || m.trade}</span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS[m.status]?.cls}`}>{STATUS[m.status]?.label}</span>
            </div>
            <h3 className="font-display font-semibold text-navy mt-3 text-lg leading-tight">{m.title}</h3>
            {m.description && <p className="text-sm text-slate-500 mt-1 flex-1">{m.description}</p>}
            <div className="mt-3 space-y-1.5 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Euro className="w-4 h-4 text-slate-400" /> <span className="font-mono">{euro(m.budget)}</span></div>
              {m.city && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {m.city}</div>}
              {m.deadline && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" /> {m.deadline}</div>}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-400">
              Par {m.poster_name}{m.assignee_name ? ` · Attribuée à ${m.assignee_name}` : ""}
            </div>
            {tab === "marketplace" && (
              <button onClick={() => apply(m.id)} data-testid={`apply-${m.id}`}
                className="mt-3 w-full py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep transition-colors inline-flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> Accepter la mission
              </button>
            )}
            {tab === "mine" && NEXT[m.status] && (
              <button onClick={() => advance(m)} data-testid={`advance-${m.id}`}
                className="mt-3 w-full py-2 rounded-xl bg-sky-400 text-navy text-sm font-semibold hover:bg-sky-500 transition-colors inline-flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Passer en « {STATUS[NEXT[m.status]].label} »
              </button>
            )}
          </div>
        ))}
        {missions.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-400">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {tab === "marketplace" ? "Aucune mission disponible pour le moment." : "Vous n'avez aucune mission."}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="mission-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-bold text-navy">Nouvelle mission de sous-traitance</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Titre de la mission" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="mission-title" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
              <textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                  {Object.entries(TRADES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input type="number" placeholder="Budget €" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <input placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <input placeholder="Échéance (ex: 15 jours)" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
            </div>
            <button onClick={create} data-testid="submit-mission"
              className="mt-5 w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors">
              Publier la mission
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
